import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { Button, Card, CardBody, CardTitle, Col, Input, Row } from "reactstrap";
import { ErrorToaster, successToaster } from "components/common/toast";
import {
  AddZoneAPI,
  DeleteZoneAPI,
  GetOrderHoursAPI,
  GetOrderSettingsAPI,
  GetZonesAPI,
  SaveOrderHoursAPI,
  SaveOrderSettingsAPI,
  SaveZoneAPI,
  StripeConnectAPI,
} from "network/orders";
import { errText } from "views/bookings/Agenda";
import { EmbedSnippets } from "components/common/EmbedSnippets";

// Campi numerici: le etichette sono in orders:settings.num.<campo>
const NUM = ["pickup_minutes", "delivery_minutes", "accept_timeout_minutes", "slot_minutes", "max_orders_per_slot", "days_ahead", "min_order"];
const NEW_ZONE = { name: "", postcodes: "", fee: "3", min_order: "0", free_over: "" };
const NEW_HOURS = { kind: "pickup", weekdays: [0, 1, 2, 3, 4, 5, 6], open_time: "12:00", close_time: "22:00" };

const OrderSettings = () => {
  const { t } = useTranslation("orders");
  const DAYS = t("days", { returnObjects: true });
  const [s, setS] = useState(null);
  const [hours, setHours] = useState([]);
  const [zones, setZones] = useState([]);
  const [zone, setZone] = useState(NEW_ZONE);
  const [row, setRow] = useState(NEW_HOURS);

  const load = async () => {
    try {
      setS(await GetOrderSettingsAPI());
      setHours(await GetOrderHoursAPI());
      setZones(await GetZonesAPI());
    } catch (e) {
      ErrorToaster(errText(e));
    }
  };
  useEffect(() => {
    load();
  }, []);
  const run = async (fn, ok) => {
    try {
      await fn();
      if (ok) successToaster(ok);
      load();
    } catch (e) {
      ErrorToaster(errText(e));
    }
  };
  const connectStripe = async () => {
    try {
      const { url } = await StripeConnectAPI(window.location.href);
      window.location.href = url;
    } catch (e) {
      ErrorToaster(errText(e));
    }
  };
  if (!s) return null;
  const set = (k, v) => setS({ ...s, [k]: v });
  const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/?$/, "/");
  const link = `${base.startsWith("http") ? base : window.location.origin + base}${s.public_path}/page`;
  const saveAll = () => {
    const { slug, public_path, widget_path, stripe_connected, stripe_ready, ...rest } = s;
    return run(() => SaveOrderSettingsAPI(rest), t("settings.saved"));
  };
  const saveHours = (list) => run(() => SaveOrderHoursAPI(list), t("settings.hoursSaved"));

  return (
    <>
      <Card>
        <CardBody>
          <CardTitle tag="h4">{t("settings.linkTitle")}</CardTitle>
          <Row className="align-items-center">
            <Col md="9">
              <Input readOnly value={link} onFocus={(e) => e.target.select()} />
              <label className="mt-3 mr-4"><input type="checkbox" checked={s.enabled} onChange={(e) => set("enabled", e.target.checked)} /> {t("settings.open")}</label>
              <label><input type="checkbox" checked={s.paused} onChange={(e) => set("paused", e.target.checked)} /> <strong>{t("settings.pauseBold")}</strong> {t("settings.pauseRest")}</label>
            </Col>
            <Col md="3" className="text-center mt-3 mt-md-0"><QRCodeSVG value={link} size={120} /></Col>
          </Row>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <CardTitle tag="h4">{t("settings.embedTitle")}</CardTitle>
          <EmbedSnippets widgetPath={s.widget_path} hostId="risto-order" label={t("settings.embedLabel")} kind="order" />
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <CardTitle tag="h4">{t("settings.rulesTitle")}</CardTitle>
          <div className="mb-3">
            <label className="mr-4"><input type="checkbox" checked={s.pickup_enabled} onChange={(e) => set("pickup_enabled", e.target.checked)} /> {t("settings.pickup")}</label>
            <label className="mr-4"><input type="checkbox" checked={s.delivery_enabled} onChange={(e) => set("delivery_enabled", e.target.checked)} /> {t("settings.delivery")}</label>
            {s.delivery_enabled && (
              <>
                <strong className="mr-2">{t("settings.deliveryWith")}</strong>
                <label className="mr-3"><input type="radio" checked={s.delivery_by === "own"} onChange={() => set("delivery_by", "own")} /> {t("settings.ownCouriers")}</label>
                <label><input type="radio" checked={s.delivery_by === "courier"} onChange={() => set("delivery_by", "courier")} /> {t("settings.externalCourier")}</label>
              </>
            )}
          </div>
          <div className="mb-3">
            <strong className="mr-2">{t("settings.acceptance")}</strong>
            <label className="mr-3"><input type="radio" checked={s.accept_mode === "manual"} onChange={() => set("accept_mode", "manual")} /> {t("settings.manual")}</label>
            <label><input type="radio" checked={s.accept_mode === "auto"} onChange={() => set("accept_mode", "auto")} /> {t("settings.auto")}</label>
          </div>
          <Row>
            {NUM.map((k) => (
              <Col md="4" key={k} className="mb-3"><label>{t(`settings.num.${k}`)}</label><Input type="number" min="0" value={s[k]} onChange={(e) => set(k, e.target.value)} /></Col>
            ))}
            <Col md="4" className="mb-3"><label>{t("settings.phone")}</label><Input value={s.phone} onChange={(e) => set("phone", e.target.value)} /></Col>
            <Col md="8" className="mb-3"><label>{t("settings.welcome")}</label><Input value={s.welcome_message} onChange={(e) => set("welcome_message", e.target.value)} /></Col>
          </Row>
          <Button color="primary" onClick={saveAll}>{t("save")}</Button>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <CardTitle tag="h4">{t("settings.payments")}</CardTitle>
          <label className="mr-4"><input type="checkbox" checked={s.cash_enabled} onChange={(e) => set("cash_enabled", e.target.checked)} /> {t("settings.cash")}</label>
          <label><input type="checkbox" checked={s.online_enabled} disabled={!s.stripe_ready} onChange={(e) => set("online_enabled", e.target.checked)} /> {t("settings.online")}</label>
          <div className="mt-2">
            {s.stripe_ready ? (
              <span className="badge badge-success">{t("settings.stripeLinked")}</span>
            ) : (
              <>
                <p className="text-muted small mb-2">
                  {s.stripe_connected
                    ? t("settings.stripeIncomplete")
                    : t("settings.stripeIntro")}
                </p>
                <Button size="sm" color="info" onClick={connectStripe}>{s.stripe_connected ? t("settings.stripeComplete") : t("settings.stripeConnect")}</Button>
              </>
            )}
          </div>
          <div className="mt-3"><Button color="primary" onClick={saveAll}>{t("save")}</Button></div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <CardTitle tag="h4">{t("settings.hoursTitle")}</CardTitle>
          {hours.length === 0 && <p className="text-muted">{t("settings.noHours")}</p>}
          {hours.map((h, i) => (
            <Row key={i} className="align-items-center py-2 border-bottom">
              <Col md="2"><strong>{h.kind === "pickup" ? t("settings.pickup") : t("settings.delivery")}</strong></Col>
              <Col md="4">{h.weekdays.map((d) => DAYS[d]).join(" ")}</Col>
              <Col md="4">{h.open_time} – {h.close_time}</Col>
              <Col md="2"><Button size="sm" color="danger" onClick={() => saveHours(hours.filter((_, n) => n !== i))}>×</Button></Col>
            </Row>
          ))}
          <Row className="mt-3">
            <Col md="2">
              <Input type="select" value={row.kind} onChange={(e) => setRow({ ...row, kind: e.target.value })}>
                <option value="pickup">{t("settings.pickup")}</option>
                <option value="delivery">{t("settings.delivery")}</option>
              </Input>
            </Col>
            <Col md="2"><Input type="time" value={row.open_time} onChange={(e) => setRow({ ...row, open_time: e.target.value })} /></Col>
            <Col md="2"><Input type="time" value={row.close_time} onChange={(e) => setRow({ ...row, close_time: e.target.value })} /></Col>
            <Col md="3"><Button size="sm" onClick={() => saveHours([...hours, row])}>{t("settings.add")}</Button></Col>
          </Row>
          <div className="mt-2">
            {DAYS.map((d, i) => (
              <label key={d} className="mr-3">
                <input type="checkbox" checked={row.weekdays.includes(i)}
                  onChange={() => setRow({ ...row, weekdays: row.weekdays.includes(i) ? row.weekdays.filter((x) => x !== i) : [...row.weekdays, i] })} /> {d}
              </label>
            ))}
          </div>
        </CardBody>
      </Card>

      {s.delivery_enabled && (
        <Card>
          <CardBody>
            <CardTitle tag="h4">{t("settings.zonesTitle")}</CardTitle>
            {zones.length === 0 && <p className="text-muted">{t("settings.noZones")}</p>}
            {zones.map((z) => (
              <Row key={z.id} className="align-items-center py-2 border-bottom">
                <Col md="2"><strong>{z.name}</strong></Col>
                <Col md="3">{t("settings.postcode")} {z.postcodes.join(", ")}</Col>
                <Col md="5">
                  {t("settings.zoneLine", { fee: z.fee, min: z.min_order })}{z.free_over != null && ` · ${t("settings.freeOver", { amount: z.free_over })}`}
                </Col>
                <Col md="2"><Button size="sm" color="danger" onClick={() => window.confirm(t("settings.deleteZone")) && run(() => DeleteZoneAPI(z.id))}>×</Button></Col>
              </Row>
            ))}
            <Row className="mt-3">
              <Col md="2"><Input placeholder={t("settings.zoneName")} value={zone.name} onChange={(e) => setZone({ ...zone, name: e.target.value })} /></Col>
              <Col md="3"><Input placeholder={t("settings.zonePostcodes")} value={zone.postcodes} onChange={(e) => setZone({ ...zone, postcodes: e.target.value })} /></Col>
              <Col md="2"><Input type="number" min="0" step="0.5" placeholder={t("settings.zoneFee")} value={zone.fee} onChange={(e) => setZone({ ...zone, fee: e.target.value })} /></Col>
              <Col md="2"><Input type="number" min="0" placeholder={t("settings.zoneMin")} value={zone.min_order} onChange={(e) => setZone({ ...zone, min_order: e.target.value })} /></Col>
              <Col md="2"><Input type="number" min="0" placeholder={t("settings.zoneFree")} value={zone.free_over} onChange={(e) => setZone({ ...zone, free_over: e.target.value })} /></Col>
              <Col md="1"><Button size="sm" onClick={() => run(() => AddZoneAPI(zone).then(() => setZone(NEW_ZONE)), t("settings.zoneAdded"))}>+</Button></Col>
            </Row>
          </CardBody>
        </Card>
      )}
    </>
  );
};

export default OrderSettings;
